import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion } from "motion/react";
import { spring } from "../lib/springs";
import { useSurface } from "../lib/surface-context";
import { surfaceClasses } from "../lib/surface-classes";
import { useFluidHover } from "../lib/use-fluid-hover";
import { FluidHoverHighlight } from "./FluidHoverHighlight";

/**
 * Segmented tabs with one indicator that slides and resizes onto the active
 * tab, instead of every option being its own filled button.
 *
 *   items      `[{ value, label, count, tone, icon }]`. `count` renders a
 *              badge (omit it for none; 0 still renders). `tone: "attention"`
 *              tints a non-zero badge, for the tab that needs someone's action
 *   value      the active item's value
 *   onChange   called with the new value
 *   label      aria-label for the tab list
 *   size       "" | "sm" — sm is the compact one that sits in a filter bar
 *
 * The indicator is a single element measured from the active button
 * (offsetLeft / offsetWidth), so it morphs between tabs of different widths
 * rather than fading between per-tab backgrounds. It travels on the moderate
 * spring (lib/springs) and sits three surface steps above the track's
 * substrate. A ResizeObserver re-measures when a count changes a tab's width.
 * The first measurement lands without travel (`initial={false}`), or the pill
 * would slide in from the left edge on page load.
 *
 * Hover is the shared fluid-hover highlight: one fill that glides between
 * tabs on the fast spring and fades in from the active tab on a fresh entry.
 * Labels reserve their semibold width (see .mt-label in theme.css), so
 * selecting a tab never shifts its neighbours.
 *
 * Keyboard: Left/Right move and select (automatic activation, as WAI-ARIA
 * recommends for tabs that only filter), Home/End jump to the ends.
 * Styled by the `.mt-*` block at the end of theme.css.
 */
const MorphTabs = ({ items, value, onChange, label, size = "", className = "" }) => {
  const listRef = useRef(null);
  const buttonRefs = useRef({});
  const [indicator, setIndicator] = useState(null);
  const hover = useFluidHover(listRef, { axis: "x" });
  const { registerItem } = hover;
  const pillLevel = Math.min(useSurface() + 3, 8);

  useLayoutEffect(() => {
    const measure = () => {
      const active = buttonRefs.current[value];
      if (!active) {
        setIndicator(null);
        return;
      }
      const left = active.offsetLeft;
      const width = active.offsetWidth;
      setIndicator((prev) =>
        prev && prev.left === left && prev.width === width ? prev : { left, width }
      );
    };

    measure();
    if (typeof ResizeObserver === "undefined" || !listRef.current) return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(listRef.current);
    Object.values(buttonRefs.current).forEach((node) => node && observer.observe(node));
    return () => observer.disconnect();
  }, [value, items]);

  // Register the tabs with the hover list. Keyed on the values rather than the
  // items array, which callers rebuild every render.
  const valuesKey = items.map((item) => item.value).join("\u0000");
  useEffect(() => {
    const values = valuesKey.split("\u0000");
    values.forEach((v, index) => registerItem(index, buttonRefs.current[v] ?? null));
    return () => values.forEach((_, index) => registerItem(index, null));
  }, [valuesKey, registerItem]);

  const select = (index) => {
    const item = items[index];
    if (!item) return;
    onChange(item.value);
    const node = buttonRefs.current[item.value];
    if (node) node.focus();
  };

  const onKeyDown = (event) => {
    const index = items.findIndex((item) => item.value === value);
    const last = items.length - 1;
    const next = {
      ArrowRight: index >= last ? 0 : index + 1,
      ArrowLeft: index <= 0 ? last : index - 1,
      Home: 0,
      End: last,
    }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    select(next);
  };

  const activeIndex = items.findIndex((item) => item.value === value);

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={label}
      className={["mt", size ? `mt-${size}` : "", className].filter(Boolean).join(" ")}
      onKeyDown={onKeyDown}
      {...hover.handlers}
    >
      {/* before the indicator, so hovering the active tab tints under it */}
      <FluidHoverHighlight
        hover={hover}
        from={activeIndex >= 0 ? hover.itemRects[activeIndex] : null}
        className="mt-hover"
      />
      {indicator ? (
        <motion.span
          className={"mt-indicator " + surfaceClasses(pillLevel)}
          aria-hidden="true"
          initial={false}
          animate={{ x: indicator.left, width: indicator.width }}
          transition={spring.moderate}
        />
      ) : null}
      {items.map((item) => {
        const selected = item.value === value;
        const hasCount = item.count !== undefined && item.count !== null && item.count !== "";
        const flagged = item.tone && Number(item.count) > 0;
        return (
          <button
            key={item.value}
            ref={(node) => {
              buttonRefs.current[item.value] = node;
            }}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-label={hasCount ? `${item.label}, ${item.count}` : item.label}
            tabIndex={selected ? 0 : -1}
            className={"mt-tab" + (selected ? " is-active" : "")}
            onClick={() => onChange(item.value)}
          >
            {item.icon ? (
              <span className="mt-icon" aria-hidden="true">
                {item.icon}
              </span>
            ) : null}
            <span className="mt-label" data-text={item.label}>
              {item.label}
            </span>
            {hasCount ? (
              <span className={"mt-count" + (flagged ? ` mt-count-${item.tone}` : "")}>
                {item.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
};

export default MorphTabs;
